/**
 * THE P-SERIES / TAP TO PAY FORM RULES for the SUNMI register/edit modal.
 *
 * Field labels and validators are READ from the existing registration panel
 * (`CLASS_FORM` in pages/inventory/DeviceSetupPanels.tsx) — never re-implemented
 * — so the two forms cannot disagree about what a device needs:
 *
 *   SPIn USB    serial · Register Id · TPN            (+ Auth Key optional, + iPOS key pair optional)
 *   SPIn Cloud  serial · Register Id · Auth Key · TPN (+ iPOS key pair optional)
 *
 * Edits go through the EXISTING routes, sending only what changed
 * (ext3 PATCH …/emv-devices/{id}: `deviceName` or a partial `deviceConfig`;
 *  PUT …/emv-devices/{id}/rtpay: `mode`, `merchantCode`). A secret is never
 * read back, so an untouched secret is never sent.
 */
import { CLASS_FORM, type FieldSpec } from '../../../pages/inventory/DeviceSetupPanels';

export type SpinKey = 'serialNumber' | 'spinRegisterId' | 'spinAuthKey' | 'spinTpn' | 'iposApiKey' | 'iposSecretKey';

/** The existing field spec for a key (labels + validators), from the cloud lane's full list. */
export function spec(key: SpinKey): FieldSpec {
  const all = [...(CLASS_FORM.dejavoo_spin_cloud?.fields || []), ...(CLASS_FORM.dejavoo_spin_usb?.fields || [])];
  return all.find((f) => f.key === key) || { key: key as any, label: key, hint: '', placeholder: '', required: false };
}

/** Required on this route, per the existing CLASS_FORM. */
export function requiredOn(route: string, key: SpinKey): boolean {
  return !!(CLASS_FORM[route]?.fields || []).find((f) => f.key === key && f.required);
}

/** Is this value acceptable for the key (empty is checked separately by `requiredOn`)? */
export function validValue(key: SpinKey, v: string): boolean {
  const s = spec(key);
  return s.valid ? s.valid(v) : !!v.trim();
}

export interface PForm {
  deviceName: string; model: string; route: string; serial: string; tpn: string;
  registerId: string; authKey: string; iposApiKey: string; iposSecretKey: string;
}

/** Problems with a P-series form, keyed by field — empty object = valid. */
export function pProblems(f: PForm, opts: { creating: boolean; authKeyTouched: boolean; iposTouched: boolean; storedAuthKey?: boolean; registerIdChanged?: boolean }): Partial<Record<SpinKey | 'deviceName' | 'model', string>> {
  const out: Partial<Record<SpinKey | 'deviceName' | 'model', string>> = {};
  if (!f.deviceName.trim()) out.deviceName = 'Required';
  if (opts.creating && !f.model) out.model = 'Required';
  if (!f.serial.trim()) out.serialNumber = 'Required';
  if (!validValue('spinTpn', f.tpn)) out.spinTpn = '12 digits';
  if (!f.registerId.trim()) out.spinRegisterId = 'Required';
  else if (!validValue('spinRegisterId', f.registerId)) out.spinRegisterId = 'That is the field’s name, not its value';
  const cloud = f.route === 'dejavoo_spin_cloud';
  if (opts.authKeyTouched || opts.creating) {
    if (f.authKey.trim()) {
      if (!validValue('spinAuthKey', f.authKey)) out.spinAuthKey = 'Not an Auth Key (a field name or the long Auth Token)';
      else if (f.authKey.trim() === f.registerId.trim()) out.spinAuthKey = 'Must differ from the Register Id';
    } else if (cloud && (opts.creating || !opts.storedAuthKey || opts.authKeyTouched)) out.spinAuthKey = 'Required on SPIn Cloud';
  }
  if (!opts.creating && cloud && opts.registerIdChanged && !f.authKey.trim() && !out.spinAuthKey) {
    out.spinAuthKey = 'Re-enter the Auth Key to change the Register Id';
  }
  if (opts.iposTouched || opts.creating) {
    const a = !!f.iposApiKey.trim(); const b = !!f.iposSecretKey.trim();
    if (a !== b) out[a ? 'iposSecretKey' : 'iposApiKey'] = 'Both or neither';
  }
  return out;
}

/** The P-series register body — only fields with a value; the route decides the lane. */
export function pSeriesRegisterBody(f: PForm) {
  const cloud = f.route === 'dejavoo_spin_cloud';
  return {
    deviceName: f.deviceName.trim(),
    model: f.model,
    paymentConfigId: f.route,
    serialNumber: f.serial.trim(),
    connectionType: cloud ? 'cloud' : 'usb',
    spinTpn: f.tpn.trim(),
    spinRegisterId: f.registerId.trim(),
    ...(f.authKey.trim() ? { spinAuthKey: f.authKey.trim() } : {}),
    ...(f.iposApiKey.trim() && f.iposSecretKey.trim() ? { iposApiKey: f.iposApiKey.trim(), iposSecretKey: f.iposSecretKey.trim() } : {}),
  };
}

/** The device's current values as the list endpoint serves them. */
export function formFromDevice(d: any): PForm {
  return {
    deviceName: d?.deviceName || '', model: d?.model || '', route: String(d?.deviceClass || ''),
    serial: d?.serialNumber || '', tpn: d?.tpn || d?.spinTpn || '', registerId: d?.spinRegisterId || '',
    authKey: '', iposApiKey: '', iposSecretKey: '',
  };
}

/**
 * THE EDIT REQUESTS — changed fields only.
 *   rename           → PATCH { deviceName }            (its own write on the route)
 *   any config field → PATCH { deviceConfig: {…} }      (a partial; untouched secrets absent)
 * The route validates the Register Id + Auth Key as a PAIR whenever either is
 * sent, so either one goes with the other's current value: the Register Id is
 * readable; the Auth Key is not, so changing the Register Id on SPIn Cloud
 * needs the Auth Key re-entered (pProblems says so).
 * `usePlatformKeys` clears the device's own iPOS pair (sends both as '').
 */
export function buildEditPatches(device: any, f: PForm, opts: { authKeyTouched: boolean; iposTouched: boolean; usePlatformKeys?: boolean }): Array<Record<string, unknown>> {
  const base = formFromDevice(device);
  const out: Array<Record<string, unknown>> = [];
  if (f.deviceName.trim() && f.deviceName.trim() !== base.deviceName) out.push({ deviceName: f.deviceName.trim() });
  const cfg: Record<string, string> = {};
  if (f.serial.trim() !== base.serial) cfg.serialNumber = f.serial.trim();
  if (f.tpn.trim() !== base.tpn) cfg.spinTpn = f.tpn.trim();
  const regChanged = f.registerId.trim() !== base.registerId;
  const keyNew = opts.authKeyTouched && !!f.authKey.trim();
  if (regChanged || keyNew) cfg.spinRegisterId = f.registerId.trim();
  if (keyNew) cfg.spinAuthKey = f.authKey.trim();
  if (opts.usePlatformKeys) { cfg.iposApiKey = ''; cfg.iposSecretKey = ''; }
  else if (opts.iposTouched && f.iposApiKey.trim() && f.iposSecretKey.trim()) {
    cfg.iposApiKey = f.iposApiKey.trim(); cfg.iposSecretKey = f.iposSecretKey.trim();
  }
  if (Object.keys(cfg).length) out.push({ deviceConfig: cfg });
  return out;
}

/** "P Series USB" / "P Series SPIn Cloud" / "Tap to Pay" — Super Admin type label. */
export function deviceTypeLabel(d: any): string {
  const cls = String(d?.deviceClass || '');
  if (cls === 'dejavoo_rubytill_pay') {
    const e = d?.rtpay?.cardEngine ?? d?.rtpayCardEngine;
    return e === 'tap_to_pay' ? 'Tap to Pay' : e === 'sunbay' ? 'RubyTill Pay CPad' : 'P Series RubyTill Pay';
  }
  if (cls === 'dejavoo_spin_usb') return 'P Series USB';
  if (cls === 'dejavoo_spin_cloud') return 'P Series SPIn Cloud';
  if (cls === 'magtek_usb') return d?.model === 'MagTek DynaFlex II Go' ? 'DynaFlex II Go' : d?.model === 'MagTek DynaFlex II PED' ? 'DynaFlex II PED' : 'MagTek (unknown model)';
  if (cls === 'sunbay_tapro') return d?.sunbayConnection === 'cable_aoa' ? 'SUNBAY USB terminal' : d?.sunbayConnection === 'sub_screen' ? 'SUNBAY CPad Pay (docked)' : 'SUNBAY CPad Pay';
  return cls || 'Unknown';
}

export const isSpinDevice = (d: any) => ['dejavoo_spin_usb', 'dejavoo_spin_cloud'].includes(String(d?.deviceClass || ''));
